import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, MapPin, ArrowRight, ArrowUpRight, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function EventsBanner() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events", { params: { upcoming: true } })
      .then((r) => setEvents(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <section className="relative" data-testid="events-banner-section">
      <div className="bg-[#0E2E24] text-[#FAFAF8] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="kicker text-[#F4A930] mb-4">Appy Community</div>
              <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight leading-[1.04]">
                More than stays — a community
                <br />that <span className="text-[#7CB342]">celebrates together.</span>
              </h2>
            </div>
            <div className="lg:col-span-4 text-[#FAFAF8]/80 leading-relaxed">
              Founder dinners, yoga Sundays, chef's tables, private socials — all hosted at our properties.
              <Link to="/events" className="mt-4 inline-flex items-center gap-1 text-[#F4A930] font-sora font-medium" data-testid="events-banner-see-all">
                See all events <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#FAFAF8] pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 -mt-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {events.map((e) => {
              const d = new Date(e.date);
              const day = d.getDate();
              const month = d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase();
              return (
                <article
                  key={e.id}
                  className="group bg-white border border-[#0E2E24]/10 rounded-2xl overflow-hidden hover-lift relative"
                  data-testid={`event-banner-card-${e.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={e.image} alt={e.title} className="img-cover zoom-img" loading="lazy" />
                    {/* Date stamp top-left */}
                    <div className="absolute top-3 left-3 bg-white rounded-xl overflow-hidden text-center min-w-[58px] shadow-md">
                      <div className="bg-[#0E2E24] text-[#F4A930] py-1 text-[10px] tracking-widest font-sora font-bold">{month}</div>
                      <div className="px-2 py-1.5 font-sora font-bold text-[#0E2E24] text-xl leading-none">{day}</div>
                    </div>
                    {/* Category badge top-right */}
                    <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#FAFAF8]/95 rounded-full text-[10px] tracking-widest uppercase font-sora font-semibold text-[#0E2E24]">
                      {e.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-sora text-base font-semibold text-[#0E2E24] leading-snug min-h-[3rem]">{e.title}</h3>
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-sora text-[#5B6B63]">
                      <MapPin size={12} className="text-[#1F5A43]" /> {e.location.split(",")[0]}
                    </div>
                    <Button
                      variant="ghost"
                      asChild
                      className="mt-4 -ml-3 text-[#1F5A43] hover:bg-[#E8F2EC] font-sora text-sm font-semibold gap-1.5"
                      data-testid={`event-banner-cta-${e.id}`}
                    >
                      <Link to="/events">
                        <Ticket size={14} /> Request invite <ArrowUpRight size={13} />
                      </Link>
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
